import React from 'react';

const FleetPage: React.FC = () => {
  // Ship SVG icons for different vessel types
  const getVesselIcon = (type: string) => {
    switch (type) {
      case 'accommodation':
        return (
          <svg className="w-12 h-12 text-ocean-600" fill="currentColor" viewBox="0 0 24 24">
            <path d="M20 16v-4H4v4h16zm0-6V6c0-1.1-.9-2-2-2H6c-1.1 0-2 .9-2 2v4h16zm-8 6h4v2h-4v-2zm-8 0h4v2H4v-2z"/>
          </svg>
        );
      case 'crew':
        return (
          <svg className="w-12 h-12 text-ocean-600" fill="currentColor" viewBox="0 0 24 24">
            <path d="M21 6.5c0 3.59-5 13.5-5 13.5s-5-9.91-5-13.5C11 3.46 13.46 1 16.5 1S22 3.46 22 6.5zM19 6.5c0-1.38-1.12-2.5-2.5-2.5S14 5.12 14 6.5s1.12 2.5 2.5 2.5S19 7.88 19 6.5zM6 22h12v-2H6v2zM3 18h18v-2H3v2z"/>
          </svg>
        );
      case 'survey':
        return (
          <svg className="w-12 h-12 text-ocean-600" fill="currentColor" viewBox="0 0 24 24">
            <path d="M9 11H7v6h2v-6zm4 0h-2v6h2v-6zm4 0h-2v6h2v-6zm2.5-9H20v2h-0.5v14c0 1.1-0.9 2-2 2H6.5c-1.1 0-2-0.9-2-2V4H4V2h0.5C5.6 2 6.5 2.9 6.5 4v1h11V4c0-1.1 0.9-2 2-2z"/>
          </svg>
        );
      case 'ahts':
        return (
          <svg className="w-12 h-12 text-ocean-600" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
          </svg>
        );
      case 'psv':
        return (
          <svg className="w-12 h-12 text-ocean-600" fill="currentColor" viewBox="0 0 24 24">
            <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z"/>
          </svg>
        );
      default:
        return (
          <svg className="w-12 h-12 text-ocean-600" fill="currentColor" viewBox="0 0 24 24">
            <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z"/>
          </svg>
        );
    }
  };

  const vesselTypes = [
    {
      name: 'Container Ships',
      type: 'accommodation',
      description: 'Large container vessels for international cargo transport',
      features: ['Massive cargo capacity', 'Global shipping routes', 'Modern container handling systems']
    },
    {
      name: 'Bulk Carriers',
      type: 'crew',
      description: 'Specialized ships for dry bulk cargo transportation',
      features: ['Heavy cargo capacity', 'Multiple cargo holds', 'Efficient loading/unloading']
    },
    {
      name: 'Tanker Vessels',
      type: 'survey',
      description: 'Professional tankers for liquid cargo and petroleum transport',
      features: ['Liquid cargo systems', 'Safety protocols', 'Environmental compliance']
    },
    {
      name: 'Cargo Ships',
      type: 'ahts',
      description: 'General cargo vessels for diverse transportation needs',
      features: ['Flexible cargo space', 'Multi-purpose capability', 'International operations']
    },
    {
      name: 'Offshore Support Vessels',
      type: 'psv',
      description: 'Specialized vessels supporting offshore operations and platforms',
      features: ['Platform supply capability', 'Offshore equipment transport', 'Weather-resistant design']
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-blue-100 to-ocean-100">
      <div className="section">
        <h1 className="section-header">Our Fleet</h1>
        
        <div className="maritime-card p-8 mb-16 text-center">
          <h2 className="text-2xl font-bold text-ocean-800 mb-4">Premium Ship Charter & Rental Fleet</h2>
          <p className="text-lg text-gray-700 max-w-4xl mx-auto leading-relaxed mb-8">
            PetroMarine operates an extensive fleet of large ships available for charter and rental services in Egypt. 
            Our vessels range from massive container ships to specialized cargo vessels, all maintained to 
            international standards and equipped with state-of-the-art navigation and safety systems for 
            reliable maritime transportation services in Egyptian waters.
          </p>
          
          {/* Video Showcase */}
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Main Fleet Video */}
              <div className="relative bg-gray-900 rounded-2xl overflow-hidden shadow-2xl">
                <div className="aspect-video bg-gradient-to-br from-ocean-600 to-ocean-800 flex items-center justify-center">
                  <div className="text-center text-white">
                    <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4 cursor-pointer hover:bg-white/30 transition-colors duration-300">
                      <svg className="w-8 h-8 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z"/>
                      </svg>
                    </div>
                    <h3 className="text-xl font-semibold mb-2">Large Ship Fleet Overview</h3>
                    <p className="text-ocean-100">Watch our massive ships navigating international waters</p>
                  </div>
                </div>
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity duration-300">
                  <p className="text-white text-sm">Click to play video</p>
                </div>
              </div>

              {/* Ship Charter Services Video */}
              <div className="relative bg-gray-900 rounded-2xl overflow-hidden shadow-2xl">
                <div className="aspect-video bg-gradient-to-br from-primary-600 to-primary-800 flex items-center justify-center">
                  <div className="text-center text-white">
                    <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4 cursor-pointer hover:bg-white/30 transition-colors duration-300">
                      <svg className="w-8 h-8 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z"/>
                      </svg>
                    </div>
                    <h3 className="text-xl font-semibold mb-2">Ship Rental & Charter</h3>
                    <p className="text-primary-100">Professional ship charter and rental services</p>
                  </div>
                </div>
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity duration-300">
                  <p className="text-white text-sm">Click to play video</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 mb-16">
          <div className="maritime-card p-8">
            <h2 className="text-2xl font-bold text-ocean-800 mb-6">Fleet Specifications</h2>
            <div className="space-y-4">
              <div className="flex justify-between items-center py-3 border-b border-gray-200">
                <span className="font-medium text-gray-700">Total Vessels</span>
                <span className="text-xl font-bold text-ocean-600">15+</span>
              </div>
              <div className="flex justify-between items-center py-3 border-b border-gray-200">
                <span className="font-medium text-gray-700">Average Age</span>
                <span className="text-xl font-bold text-ocean-600">&lt; 10 Years</span>
              </div>
              <div className="flex justify-between items-center py-3 border-b border-gray-200">
                <span className="font-medium text-gray-700">Operational Availability</span>
                <span className="text-xl font-bold text-green-600">98%</span>
              </div>
              <div className="flex justify-between items-center py-3">
                <span className="font-medium text-gray-700">Safety Rating</span>
                <span className="text-xl font-bold text-green-600">A+</span>
              </div>
            </div>
          </div>

          <div className="maritime-card p-8">
            <h2 className="text-2xl font-bold text-ocean-800 mb-6">Fleet Management</h2>
            <ul className="space-y-4 text-gray-700">
              <li className="flex items-start">
                <span className="w-2 h-2 bg-ocean-500 rounded-full mr-3 mt-2"></span>
                <span>Preventive maintenance programs ensuring maximum uptime</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-ocean-500 rounded-full mr-3 mt-2"></span>
                <span>Real-time monitoring and tracking systems</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-ocean-500 rounded-full mr-3 mt-2"></span>
                <span>Qualified and certified crew members</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-ocean-500 rounded-full mr-3 mt-2"></span>
                <span>24/7 operational support and emergency response</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-ocean-500 rounded-full mr-3 mt-2"></span>
                <span>Compliance with international maritime regulations</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mb-16">
          <h2 className="text-2xl font-bold text-ocean-800 mb-8 text-center">Vessel Types</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {vesselTypes.map((vessel, index) => (
              <div key={index} className="maritime-card p-6">
                <div className="w-20 h-20 bg-ocean-50 rounded-xl flex items-center justify-center mx-auto mb-4 border-2 border-ocean-200">
                  {getVesselIcon(vessel.type)}
                </div>
                <h3 className="text-xl font-semibold text-ocean-800 mb-3 text-center">{vessel.name}</h3>
                <p className="text-gray-600 mb-4 text-center">{vessel.description}</p>
                <ul className="space-y-2">
                  {vessel.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center text-sm text-gray-700">
                      <span className="w-1.5 h-1.5 bg-ocean-500 rounded-full mr-2"></span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="maritime-card p-8 mb-16">
          <h2 className="text-2xl font-bold text-ocean-800 mb-6 text-center">Safety & Compliance</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                </svg>
              </div>
              <h3 className="font-semibold text-ocean-800 mb-2">ISM Certified</h3>
              <p className="text-gray-600 text-sm">International Safety Management</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                </svg>
              </div>
              <h3 className="font-semibold text-ocean-800 mb-2">MLC Compliant</h3>
              <p className="text-gray-600 text-sm">Maritime Labour Convention</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
              </div>
              <h3 className="font-semibold text-ocean-800 mb-2">SOLAS Certified</h3>
              <p className="text-gray-600 text-sm">Safety of Life at Sea</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                </svg>
              </div>
              <h3 className="font-semibold text-ocean-800 mb-2">MARPOL Compliant</h3>
              <p className="text-gray-600 text-sm">Marine Pollution Prevention</p>
            </div>
          </div>
        </div>

        <div className="maritime-card p-8 text-center">
          <h2 className="text-2xl font-bold text-ocean-800 mb-4">Ship Charter & Rental Services</h2>
          <p className="text-gray-700 mb-6 max-w-3xl mx-auto leading-relaxed">
            Our extensive fleet of large ships is available for charter and rental services in Egypt. 
            From container ships to specialized cargo vessels, we provide reliable maritime transportation 
            solutions for your business needs in Egyptian waters. Contact us to discuss your specific ship rental requirements 
            and availability for Egyptian ports and routes.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="btn-primary">
              Request Ship Charter Quote
            </button>
            <button className="btn-secondary">
              View Available Ships
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FleetPage;